import React, { useState, useEffect, useCallback } from 'react';

function Profile({ tgId, onUpdateNickname, onInitialLoad }) {
  const [data, setData] = useState({ nickname: '', address: '', warns: 0 });
  const [orig, setOrig] = useState({ nickname: '', address: '', warns: 0 });
  const [edit, setEdit] = useState({ address: false });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [stats, setStats] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`/api/profile/${tgId}`);
      const json = await res.json();
      if (res.ok) {
        const loaded = {
          nickname: json.mc_nickname || '',
          address: json.postal_address || '',
          warns: json.warns || 0
        };
        setOrig(prev => {
          if (prev.nickname !== loaded.nickname || prev.address !== loaded.address || prev.warns !== loaded.warns) {
            setData(currentData => {
              if (!edit.address) return loaded;
              return { ...currentData, nickname: loaded.nickname, warns: loaded.warns };
            });
            return loaded;
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Ошибка загрузки профиля:', err);
    }
  }, [tgId, edit]);

  // Никнейм теперь приходит из API (см. App.js) — просто держим шапку приложения
  // в курсе актуального значения, когда профиль подгрузился/обновился.
  useEffect(() => {
    if (orig.nickname) onUpdateNickname(orig.nickname);
  }, [orig.nickname, onUpdateNickname]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`/api/profile/${tgId}/stats`);
      const json = await res.json();
      if (res.ok) setStats(json);
    } catch (err) {
      console.error('Ошибка загрузки статистики:', err);
    }
  }, [tgId]);

  useEffect(() => {
    fetchProfile().finally(() => { setLoading(false); onInitialLoad && onInitialLoad(); });
    const interval = setInterval(fetchProfile, 2000);
    return () => clearInterval(interval);
  }, [fetchProfile]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 8000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleSaveClick = (stateField, dbField) => {
    const val = data[stateField];
    const oldVal = orig[stateField];
    
    if (val === oldVal) {
      setEdit(prev => ({ ...prev, [stateField]: false }));
      return;
    }

    saveDataToServer(dbField, stateField, val);
  };

  const saveDataToServer = async (dbField, stateField, val) => {
    setMsg('Сохранение...');
    try {
      const res = await fetch(`/api/profile/${tgId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field: dbField, value: val })
      });
      const json = await res.json();
      
      if (!res.ok) {
        setMsg(json.error || 'Ошибка сохранения');
      } else {
        setMsg('Успешно сохранено!');
        setOrig(prev => ({ ...prev, [stateField]: val }));
        setEdit(prev => ({ ...prev, [stateField]: false }));
      }
    } catch (err) {
      setMsg('Ошибка соединения с сервером');
    }
  };

  if (loading) return <div className="card" style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Загрузка...</div>;

  return (
    <div>
      <h2 style={{marginTop: 0}}>Профиль</h2>
      
      {data.warns > 0 && (
        <div className="card" style={{ textAlign: 'center', marginBottom: '15px', padding: '10px' }}>
          <p style={{ margin: 0, color: 'var(--warning)', fontWeight: 'bold', fontSize: '14px' }}>
            Варны: {data.warns}/3
          </p>
        </div>
      )}

      {stats && orig.nickname && (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch', marginBottom: '15px' }}>
          <div className="card" style={{ flex: '0 0 130px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
            <img
              src={`https://minotar.net/armor/bust/${encodeURIComponent(orig.nickname)}/260.png`}
              alt=""
              style={{ width: '100%', maxWidth: '130px', imageRendering: 'pixelated', borderRadius: '10px' }}
              onError={(e) => { e.target.style.visibility = 'hidden'; }}
            />
          </div>

          <div className="card" style={{ flex: 1, padding: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: (stats.lots_sold > 0 || stats.active_listings > 0) ? '1px solid var(--border)' : 'none' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Куплено лотов</span>
              <b style={{ fontSize: '15px' }}>{stats.lots_bought}</b>
            </div>
            {(stats.lots_sold > 0 || stats.active_listings > 0) && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Продано лотов</span>
                  <b style={{ fontSize: '15px' }}>{stats.lots_sold}</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Заработано сегодня</span>
                  <b style={{ fontSize: '15px', color: 'var(--success)' }}>{stats.earned_today} сц</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Заработано всего</span>
                  <b style={{ fontSize: '15px', color: 'var(--success)' }}>{stats.earned_total} сц</b>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <label>Никнейм Minecraft:</label>
        <input type="text" value={data.nickname} disabled readOnly />
      </div>

      <div className="card">
        <label>Почтовый адрес на MP:</label>
        <input 
          type="text" 
          value={data.address} 
          disabled={!edit.address}
          onChange={(e) => setData({...data, address: e.target.value})} 
        />
        {edit.address ? (
          <button className="primary-btn" onClick={() => handleSaveClick('address', 'postal_address')}>Сохранить изменения</button>
        ) : (
          <button className="secondary-btn" onClick={() => setEdit(prev => ({ ...prev, address: true }))}>
            {orig.address ? 'Изменить' : 'Добавить'}
          </button>
        )}
      </div>

      {msg && <div style={{textAlign: 'center', margin: '10px 0', color: 'var(--accent-dark)'}}>{msg}</div>}
    </div>
  );
}

export default Profile;
